import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import ProtectedRoute from './components/ProtectedRoute'

// Public Pages
import Home from './pages/Home'
import Login from './pages/Login'
import Register from './pages/Register'
import NotFound from './pages/NotFound'
import StaffLogin from './pages/StaffLogin'
import PlatformLogin from './pages/PlatformLogin'
import AuthCallback from './pages/AuthCallback'
import RegisterComplete from './pages/RegisterComplete'
import VerifyMember from './pages/Verify'
import ChangePassword from './pages/ChangePassword'

// Supporter Pages
import Dashboard from './pages/Dashboard'
import MembershipCard from './pages/MembershipCard'
import Fixtures from './pages/Fixtures'
import Welfare from './pages/Welfare'
import Profile from './pages/Profile'
import Subscribe from './pages/Subscribe'

// Shop Pages
import Shop from './pages/Shop'
import ShopSuccess from './pages/Shop/components/Success'
import ShopCancel from './pages/Shop/components/Cancel'

// Admin Shop
import AdminShop from './pages/Admin/Shop'

// Policies
import Policies from './pages/Policies'

// Admin Pages
import AdminOverview from './pages/Admin/Overview'
import AdminMembers from './pages/Admin/Members'
import AdminChapters from './pages/Admin/Chapters'
import AdminFixtures from './pages/Admin/Fixtures'
import AdminWelfare from './pages/Admin/Welfare'
import AdminSettings from './pages/Admin/Settings'
import AdminInvite from './pages/Admin/Invite'
import AdminSubscriptions from './pages/Admin/Subscriptions'
import AdminReports from './pages/Admin/Reports'
import AdminAnalytics from './pages/Admin/Analytics'
import AdminScan from './pages/AdminScan'

// Super Admin Pages
import SuperAdminOverview from './pages/SuperAdmin/Overview'
import SuperAdminClubs from './pages/SuperAdmin/Clubs'
import SuperAdminMembers from './pages/SuperAdmin/Members'
import SuperAdminInvite from './pages/SuperAdmin/Invite'
import SuperAdminSettings from './pages/SuperAdmin/Settings'

// Coordinator Pages
import CoordinatorOverview from './pages/Coordinator/Overview'
import CoordinatorMembers from './pages/Coordinator/Members'

function App() {
  return (
    <Router>
      <Routes>

        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/staff/login" element={<StaffLogin />} />
        <Route path="/platform/login" element={<PlatformLogin />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/register/complete" element={<RegisterComplete />} />
        <Route path="/verify/:membershipId" element={<VerifyMember />} />
        <Route path="/change-password" element={<ChangePassword />} />

        {/* Shop Routes — public browse, login required for checkout */}
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/success" element={<ShopSuccess />} />
        <Route path="/shop/cancel" element={<ShopCancel />} />

        {/* Policies */}
        <Route path="/policies" element={<Policies />} />

        {/* Supporter Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute allowedRoles={['supporter']}>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/membership-card" element={
          <ProtectedRoute allowedRoles={['supporter']}>
            <MembershipCard />
          </ProtectedRoute>
        } />
        <Route path="/fixtures" element={
          <ProtectedRoute allowedRoles={['supporter']}>
            <Fixtures />
          </ProtectedRoute>
        } />
        <Route path="/welfare" element={
          <ProtectedRoute allowedRoles={['supporter']}>
            <Welfare />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute allowedRoles={['supporter']}>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="/subscribe" element={
          <ProtectedRoute allowedRoles={['supporter']}>
            <Subscribe />
          </ProtectedRoute>
        } />

        {/* Club Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={['club_admin']}>
            <AdminOverview />
          </ProtectedRoute>
        } />
        <Route path="/admin/members" element={
          <ProtectedRoute allowedRoles={['club_admin']}>
            <AdminMembers />
          </ProtectedRoute>
        } />
        <Route path="/admin/subscriptions" element={
          <ProtectedRoute allowedRoles={['club_admin']}>
            <AdminSubscriptions />
          </ProtectedRoute>
        } />
        <Route path="/admin/reports" element={
          <ProtectedRoute allowedRoles={['club_admin']}>
            <AdminReports />
          </ProtectedRoute>
        } />
        <Route path="/admin/analytics" element={
          <ProtectedRoute allowedRoles={['club_admin']}>
            <AdminAnalytics />
          </ProtectedRoute>
        } />
        <Route path="/admin/chapters" element={
          <ProtectedRoute allowedRoles={['club_admin']}>
            <AdminChapters />
          </ProtectedRoute>
        } />
        <Route path="/admin/fixtures" element={
          <ProtectedRoute allowedRoles={['club_admin']}>
            <AdminFixtures />
          </ProtectedRoute>
        } />
        <Route path="/admin/welfare" element={
          <ProtectedRoute allowedRoles={['club_admin']}>
            <AdminWelfare />
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute allowedRoles={['club_admin']}>
            <AdminSettings />
          </ProtectedRoute>
        } />
        <Route path="/admin/invite" element={
          <ProtectedRoute allowedRoles={['club_admin']}>
            <AdminInvite />
          </ProtectedRoute>
        } />
        <Route path="/admin/shop" element={
          <ProtectedRoute allowedRoles={['club_admin']}>
            <AdminShop />
          </ProtectedRoute>
        } />
        <Route path="/admin/scan" element={
          <ProtectedRoute allowedRoles={['club_admin', 'chapter_coordinator', 'super_admin']}>
            <AdminScan />
          </ProtectedRoute>
        } />

        {/* Super Admin Routes */}
        <Route path="/superadmin" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminOverview />
          </ProtectedRoute>
        } />
        <Route path="/superadmin/clubs" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminClubs />
          </ProtectedRoute>
        } />
        <Route path="/superadmin/members" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminMembers />
          </ProtectedRoute>
        } />
        <Route path="/superadmin/invite" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminInvite />
          </ProtectedRoute>
        } />
        <Route path="/superadmin/settings" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminSettings />
          </ProtectedRoute>
        } />

        {/* Chapter Coordinator Routes */}
        <Route path="/coordinator" element={
          <ProtectedRoute allowedRoles={['chapter_coordinator']}>
            <CoordinatorOverview />
          </ProtectedRoute>
        } />
        <Route path="/coordinator/members" element={
          <ProtectedRoute allowedRoles={['chapter_coordinator']}>
            <CoordinatorMembers />
          </ProtectedRoute>
        } />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />

      </Routes>
    </Router>
  )
}

export default App